import { endOfDay, isSameDay, startOfDay } from "date-fns";
import { parseLocalDateString } from "./parseLocalDate";

export const ALL_JS_DAYS = [0, 1, 2, 3, 4, 5, 6];

const parseDayNumber = (day) => {
  if (day === null || day === undefined || day === "" || typeof day === "boolean") {
    return NaN;
  }
  return Number(day);
};

export const apiDayToJsDay = (apiDay) => {
  const normalized = parseDayNumber(apiDay);
  if (!Number.isInteger(normalized)) return NaN;
  return normalized === 7 ? 0 : normalized;
};

export const jsDayToApiDay = (jsDay) => {
  const normalized = parseDayNumber(jsDay);
  if (!Number.isInteger(normalized)) return NaN;
  return normalized === 0 ? 7 : normalized;
};

export const apiDayToCanonicalDay = (apiDay) => jsDayToApiDay(apiDayToJsDay(apiDay));

export const formatLocalDate = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getAvailabilityItems = (bookingStudioData) => {
  const items = bookingStudioData?.availabilities?.items;
  return Array.isArray(items) ? items.filter(Boolean) : [];
};

export const getBlockedTimeItems = (bookingStudioData) => {
  const items = bookingStudioData?.blockedTimes?.items;
  return Array.isArray(items) ? items.filter(Boolean) : [];
};

export const BOOKING_BUSY_STATUSES = new Set(["PENDING", "CONFIRMED"]);
export const BOOKING_SLOT_BUSY_STATUSES = new Set(["RESERVING", "HELD", "CONFIRMED"]);

export const isBusyBookingStatus = (status) => BOOKING_BUSY_STATUSES.has(status);
export const isBusyBookingSlotStatus = (status) => BOOKING_SLOT_BUSY_STATUSES.has(status);

export const hasUnavailableBusyIntervals = (bookingStudioData) =>
  Boolean(bookingStudioData?.busyIntervalsUnavailable);

export const getBusyBookingItems = (bookingStudioData) => {
  const items = bookingStudioData?.bookings?.items;
  return Array.isArray(items)
    ? items.filter(
        (booking) =>
          isBusyBookingStatus(booking?.status) &&
          booking?.startDateTime &&
          booking?.endDateTime
      )
    : [];
};

export const getBusyIntervalItems = (bookingStudioData) => {
  const slotItems = bookingStudioData?.bookingSlots?.items;
  if (Array.isArray(slotItems)) {
    return slotItems.filter(
      (slot) =>
        isBusyBookingSlotStatus(slot?.slotStatus) &&
        slot?.startDateTime &&
        slot?.endDateTime
    );
  }

  return getBusyBookingItems(bookingStudioData);
};

export const hasBusyIntervalOnDate = (date, bookingStudioData) => {
  const dateObj = parseDateInput(date);
  if (!dateObj) return false;

  return getBusyIntervalItems(bookingStudioData).some((item) => {
    const itemDate = new Date(item.startDateTime);
    return isSameDay(dateObj, itemDate);
  });
};

const getPackageItems = (bookingStudioData) => {
  const packages = bookingStudioData?.packages;
  return Array.isArray(packages) ? packages.filter(Boolean) : [];
};

export const getBookingDurationHours = (bookingStudioData, bookingData = {}) => {
  const explicitDuration = Number(bookingData?.packageDurationHours);
  if (explicitDuration > 0) return explicitDuration;

  const packages = getPackageItems(bookingStudioData);
  if (bookingStudioData?.pricingMode === "PACKAGES" && packages.length > 0) {
    const selectedPackage = packages.find((pkg) => pkg?.id === bookingData?.packageId);
    const selectedDuration = Number(selectedPackage?.durationHours);
    if (selectedDuration > 0) return selectedDuration;

    const shortestPackageDuration = packages
      .map((pkg) => Number(pkg?.durationHours))
      .filter((duration) => duration > 0)
      .sort((a, b) => a - b)[0];

    if (shortestPackageDuration > 0) return shortestPackageDuration;
  }

  const minDuration = Number(bookingStudioData?.minNumberOfHours);
  return minDuration > 0 ? minDuration : 1;
};

export const startsAtStartOfDay = (date) =>
  date.getHours() === 0 && date.getMinutes() === 0;

export const endsAtEndOfDay = (date) =>
  date.getHours() === 23 && date.getMinutes() === 59;

export const isFullDayAvailability = (availability) =>
  availability?.startTime === "00:00" && availability?.endTime === "23:59";

const parseDateInput = (date) => {
  if (date instanceof Date) return date;
  return parseLocalDateString(date);
};

export const timeToMinutes = (time, { endOfRange = false } = {}) => {
  const [rawHour, rawMinute] = String(time || "00:00").split(":").map(Number);
  if (rawHour === 24) return 24 * 60;
  if (endOfRange && rawHour === 0 && rawMinute === 0) return 24 * 60;
  if (endOfRange && rawHour === 23 && rawMinute === 59) return 24 * 60;
  return rawHour * 60 + rawMinute;
};

const minutesToTime = (minutes) => {
  if (minutes >= 24 * 60) return "24:00";
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

export const getAvailabilityRangesForDate = (date, bookingStudioData) => {
  const dateObj = parseDateInput(date);
  if (!dateObj) return [];

  const availabilityItems = getAvailabilityItems(bookingStudioData);
  if (availabilityItems.length === 0) return [];

  const jsDay = dateObj.getDay();
  const dayAvailabilities = availabilityItems.filter(
    (availability) => apiDayToJsDay(availability.dayOfWeek) === jsDay
  );

  if (dayAvailabilities.length === 0) return [];

  const seenRanges = new Set();
  const ranges = [];

  dayAvailabilities.forEach((availability) => {
    const start = availability.startTime;
    const end = isFullDayAvailability(availability)
      ? "24:00"
      : availability.endTime;

    if (!start || !end) return;

    const key = `${start}-${end}`;
    if (seenRanges.has(key)) return;

    seenRanges.add(key);
    ranges.push({ start, end });
  });

  const rangesWithDuration = ranges.map((range) => {
    const start = timeToMinutes(range.start);
    const end = timeToMinutes(range.end, { endOfRange: true });
    return { ...range, startMinutes: start, endMinutes: end, duration: end - start };
  });

  return rangesWithDuration
    .filter((current, index) => {
      const containsSmallerRange = rangesWithDuration.some((other, otherIndex) => {
        if (index === otherIndex) return false;
        return (
          current.startMinutes <= other.startMinutes &&
          current.endMinutes >= other.endMinutes &&
          current.duration > other.duration
        );
      });
      return !containsSmallerRange;
    })
    .map(({ start, end }) => ({ start, end }));
};

export const hasSufficientAvailabilityForDate = (
  date,
  bookingStudioData,
  durationHours = 1
) => {
  const durationMinutes = Math.max(Number(durationHours) || 1, 1) * 60;
  return getAvailabilityRangesForDate(date, bookingStudioData).some((range) => {
    const start = timeToMinutes(range.start);
    const end = timeToMinutes(range.end, { endOfRange: true });
    return end - start >= durationMinutes;
  });
};

export const isBlockedTimeFullyBlockingDate = (date, blockedTime) => {
  const dateObj = parseDateInput(date);
  if (!dateObj || !blockedTime?.startDateTime || !blockedTime?.endDateTime) {
    return false;
  }

  const startDate = new Date(blockedTime.startDateTime);
  const endDate = new Date(blockedTime.endDateTime);
  const dayStart = startOfDay(dateObj);
  const blockStartDay = startOfDay(startDate);
  const blockEndDay = startOfDay(endDate);

  if (blockedTime.isFullDay) {
    return dayStart >= blockStartDay && dayStart <= blockEndDay;
  }

  if (dayStart < blockStartDay || dayStart > blockEndDay) {
    return false;
  }

  if (isSameDay(startDate, endDate)) {
    return (
      isSameDay(dateObj, startDate) &&
      startsAtStartOfDay(startDate) &&
      endsAtEndOfDay(endDate)
    );
  }

  if (isSameDay(dateObj, startDate)) {
    return startsAtStartOfDay(startDate);
  }

  if (isSameDay(dateObj, endDate)) {
    return endsAtEndOfDay(endDate);
  }

  return dayStart > blockStartDay && dayStart < blockEndDay;
};

export const isDateFullyBlocked = (date, bookingStudioData) =>
  getBlockedTimeItems(bookingStudioData).some((blockedTime) =>
    isBlockedTimeFullyBlockingDate(date, blockedTime)
  );

export const getBlockedDateIntervals = (bookingStudioData) => {
  const intervals = [];

  getBlockedTimeItems(bookingStudioData).forEach((blockedTime) => {
    if (!blockedTime?.startDateTime || !blockedTime?.endDateTime) return;

    const startDate = new Date(blockedTime.startDateTime);
    const endDate = new Date(blockedTime.endDateTime);
    let intervalStart = startOfDay(startDate);
    let intervalEnd = endOfDay(endDate);

    if (!blockedTime.isFullDay) {
      if (!startsAtStartOfDay(startDate)) {
        intervalStart = startOfDay(
          new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1)
        );
      }

      if (!endsAtEndOfDay(endDate)) {
        intervalEnd = endOfDay(
          new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - 1)
        );
      }
    }

    if (intervalStart <= intervalEnd) {
      intervals.push({ start: intervalStart, end: intervalEnd });
    }
  });

  return intervals;
};

export const getWeeklyExcludedDays = (
  bookingStudioData,
  { requireSufficientSlots = false, durationHours = 1 } = {}
) => {
  const availabilityItems = getAvailabilityItems(bookingStudioData);
  if (availabilityItems.length === 0) return ALL_JS_DAYS;

  const availableJsDays = [
    ...new Set(availabilityItems.map((item) => apiDayToJsDay(item.dayOfWeek))),
  ];

  const daysWithoutAvailability = ALL_JS_DAYS.filter(
    (day) => !availableJsDays.includes(day)
  );

  if (!requireSufficientSlots) return daysWithoutAvailability;

  const daysWithInsufficientTime = ALL_JS_DAYS.filter((day) => {
    if (daysWithoutAvailability.includes(day)) return false;

    const referenceSunday = new Date(2024, 0, 7);
    const referenceDate = new Date(referenceSunday);
    referenceDate.setDate(referenceSunday.getDate() + day);

    return !hasSufficientAvailabilityForDate(referenceDate, bookingStudioData, durationHours);
  });

  return [...daysWithoutAvailability, ...daysWithInsufficientTime];
};

const combineDateAndMinutes = (dateString, minutes) => {
  const date = parseLocalDateString(dateString);
  if (!date) return null;

  const combined = new Date(date);
  combined.setHours(0, minutes, 0, 0);
  return combined;
};

const intervalsOverlap = (start, end, otherStart, otherEnd) =>
  start < otherEnd && end > otherStart;

export const isDateBookableByCalendar = (
  date,
  bookingStudioData,
  { durationHours = 1 } = {}
) => {
  if (!parseDateInput(date)) return false;
  if (isDateFullyBlocked(date, bookingStudioData)) return false;
  return hasSufficientAvailabilityForDate(date, bookingStudioData, durationHours);
};

export const isBookingRangeAvailable = (
  date,
  startTime,
  durationHours,
  bookingStudioData,
  { includeCurrentTimeBuffer = true } = {}
) => {
  const dateObj = parseDateInput(date);
  if (!dateObj || !startTime || isDateFullyBlocked(dateObj, bookingStudioData)) {
    return false;
  }

  if (hasUnavailableBusyIntervals(bookingStudioData)) return false;

  const dateString = formatLocalDate(dateObj);
  const startMinutes = timeToMinutes(startTime);
  const durationMinutes = Math.max(Number(durationHours) || 1, 1) * 60;
  const endMinutes = startMinutes + durationMinutes;

  const fitsAvailability = getAvailabilityRangesForDate(dateObj, bookingStudioData).some(
    (range) => {
      const rangeStart = timeToMinutes(range.start);
      const rangeEnd = timeToMinutes(range.end, { endOfRange: true });
      return startMinutes >= rangeStart && endMinutes <= rangeEnd;
    }
  );

  if (!fitsAvailability) return false;

  const candidateStart = combineDateAndMinutes(dateString, startMinutes);
  const candidateEnd = combineDateAndMinutes(dateString, endMinutes);
  if (!candidateStart || !candidateEnd) return false;

  if (includeCurrentTimeBuffer) {
    const now = new Date();
    const bufferTime = new Date(now.getTime() + 30 * 60 * 1000);
    const isToday = startOfDay(dateObj).getTime() === startOfDay(now).getTime();

    if (isToday && candidateStart <= bufferTime) {
      return false;
    }
  }

  const overlapsBlockedTime = getBlockedTimeItems(bookingStudioData).some((blocked) => {
    if (!blocked?.startDateTime || !blocked?.endDateTime) return false;
    if (blocked.isFullDay && isBlockedTimeFullyBlockingDate(dateObj, blocked)) {
      return true;
    }

    return intervalsOverlap(
      candidateStart,
      candidateEnd,
      new Date(blocked.startDateTime),
      new Date(blocked.endDateTime)
    );
  });

  if (overlapsBlockedTime) return false;

  const overlapsBusyInterval = getBusyIntervalItems(bookingStudioData).some((interval) =>
    intervalsOverlap(
      candidateStart,
      candidateEnd,
      new Date(interval.startDateTime),
      new Date(interval.endDateTime)
    )
  );

  return !overlapsBusyInterval;
};

export const hasBookableTimeSlot = (
  date,
  bookingStudioData,
  { durationHours = 1, includeCurrentTimeBuffer = true } = {}
) => {
  const dateObj = parseDateInput(date);
  if (!dateObj || isDateFullyBlocked(dateObj, bookingStudioData)) return false;
  if (hasUnavailableBusyIntervals(bookingStudioData)) return false;

  const durationMinutes = Math.max(Number(durationHours) || 1, 1) * 60;
  const ranges = getAvailabilityRangesForDate(dateObj, bookingStudioData);
  if (ranges.length === 0) return false;

  for (const range of ranges) {
    const startMinutes = timeToMinutes(range.start);
    const endMinutes = timeToMinutes(range.end, { endOfRange: true });
    const latestStartMinutes = endMinutes - durationMinutes;

    for (
      let minutes = Math.ceil(startMinutes / 60) * 60;
      minutes <= latestStartMinutes;
      minutes += 60
    ) {
      const time = minutesToTime(minutes);
      if (
        isBookingRangeAvailable(dateObj, time, durationHours, bookingStudioData, {
          includeCurrentTimeBuffer,
        })
      ) {
        return true;
      }
    }
  }

  return false;
};
